import { CompetencyDTO } from 'src/types/dto';
import { ActivityCompetencyDTO } from 'src/types/dto/competency.dto';
import { AuthorizationDTO } from 'src/types/dto/authorization.dto';
import { TFunction } from 'i18next';
import { IAuthorizationItem } from 'src/types/authorization';
import type { ICompetencyItem } from 'src/types/competency';
import i18next from 'i18next';
import type { ActivityDTO } from 'src/types/dto/activity.dto';
import type { IAccreditationBody } from 'src/types/accreditation-body';
import { CompetencyHierarchy } from './competency-hierarchy';

export const getCompetencyName = (competency: CompetencyDTO) => {
  return [competency?.name, competency?.sector].filter(Boolean).join('/');
};

/** Stable key for deduplicating authorizations by displayed competency label (name/sector/level). */
export const getAuthorizationDedupeKey = (
  authorization: Pick<AuthorizationDTO, 'level'> & {
    competency?: { id?: number; name?: string; sector?: string };
  }
): string => {
  const competencyName = authorization.competency?.name?.trim() ?? '';
  const sectorName = authorization.competency?.sector?.trim() ?? '';
  const levelName = authorization.level?.trim() ?? '';

  if (!competencyName) {
    return String(authorization.competency?.id ?? authorization);
  }

  if (sectorName) {
    const base = `${competencyName} / ${sectorName}`;
    return levelName ? `${base} - ${levelName}` : base;
  }

  return levelName ? `${competencyName} - ${levelName}` : competencyName;
};

export const getAuthorizationName = (
  authorization: AuthorizationDTO | IAuthorizationItem,
  i18n: any
) => {
  const competencyName = authorization?.competency?.name?.trim() ?? '';
  const sectorName = authorization?.competency?.sector?.trim() ?? '';
  const accreditationBodyName = authorization?.accreditationBody?.name?.trim() ?? '';
  const levelName = authorization?.level ?? '';
  const translatedLevelName = levelName
    ? i18n.exists(`organization:project-roles.levels.${levelName}`)
      ? i18n.t(`organization:project-roles.levels.${levelName}`)
      : levelName
    : '';

  const parts: string[] = [];

  if (competencyName) {
    parts.push(sectorName ? `${competencyName} / ${sectorName}` : competencyName);
  }

  if (accreditationBodyName) {
    parts.push(accreditationBodyName);
  }

  if (translatedLevelName) {
    parts.push(translatedLevelName);
  }

  return parts.join(' - ');
};

export function authorizationMatchesActivityCompetencyIds(
  authorization: Pick<IAuthorizationItem, 'competency'>,
  activityCompetencyIds: number[] | undefined,
  competencyHierarchy?: CompetencyHierarchy,
): boolean {
  const competencyId = authorization?.competency?.id;
  if (typeof competencyId !== 'number' || !activityCompetencyIds?.length) {
    return false;
  }

  if (activityCompetencyIds.includes(competencyId)) {
    return true;
  }

  if (competencyHierarchy) {
    return activityCompetencyIds.some((requiredId) =>
      competencyHierarchy.heldCompetencySatisfiesRequired(requiredId, competencyId),
    );
  }

  const ancestorIds =
    authorization.competency?.ancestorCompetencyIds ??
    (authorization.competency?.parentCompetencyId != null
      ? [authorization.competency.parentCompetencyId]
      : []);

  return ancestorIds.some((ancestorId) => activityCompetencyIds.includes(ancestorId));
}

/** Accreditation body to show on an activity competency chip when unambiguous. */
export function resolveAccreditationBodyForActivityCompetency(
  activity: ActivityDTO | undefined,
): IAccreditationBody | undefined {
  const bodies = [
    ...(activity?.accreditationBodies ?? []),
    ...(activity?.standard?.accreditationBodies ?? []),
  ].filter((body): body is IAccreditationBody => body?.id != null && Boolean(body?.name?.trim()));

  const uniqueBodies = Array.from(new Map(bodies.map((body) => [body.id, body])).values());
  return uniqueBodies.length === 1 ? uniqueBodies[0] : undefined;
}

export const getActivityCompetencyRequirementName = (
  activityCompetency: ActivityCompetencyDTO,
  i18n: Parameters<typeof getAuthorizationName>[1],
  accreditationBody?: Pick<IAccreditationBody, 'name'> | null,
) =>
  getAuthorizationName(
    {
      competency: activityCompetency.competency,
      level: activityCompetency.level,
      accreditationBody: accreditationBody ?? undefined,
    } as AuthorizationDTO,
    i18n,
  );

/** Merge competency rows, preferring populated catalog fields without losing selection metadata. */
export function mergeCompetencyItems(
  primary: ICompetencyItem,
  secondary?: ICompetencyItem | null,
): ICompetencyItem {
  if (!secondary) {
    return primary;
  }

  return {
    ...primary,
    ...secondary,
    name: secondary.name ?? primary.name,
    description: secondary.description ?? primary.description,
    externalId: secondary.externalId ?? primary.externalId,
    sector: secondary.sector ?? primary.sector,
    categories: secondary.categories ?? primary.categories,
    level: primary.level ?? secondary.level,
  };
}

/** Assigned competencies first (in selection order), then the rest in their existing order. */
export function reorderCompetenciesWithPinnedTop(
  competencies: ICompetencyItem[],
  pinnedCompetencies: ICompetencyItem[],
): ICompetencyItem[] {
  if (pinnedCompetencies.length === 0) {
    return competencies;
  }

  const byId = new Map(competencies.map((competency) => [competency.id, competency]));
  const pinnedIds = new Set(pinnedCompetencies.map((competency) => competency.id));
  const pinned: ICompetencyItem[] = [];

  for (const selected of pinnedCompetencies) {
    pinned.push(mergeCompetencyItems(selected, byId.get(selected.id)));
  }

  const rest = competencies.filter((competency) => !pinnedIds.has(competency.id));
  return [...pinned, ...rest];
}

export function filterCompetenciesBySearch(
  competencies: ICompetencyItem[],
  searchValue: string,
): ICompetencyItem[] {
  const search = searchValue.trim().toLowerCase();
  if (!search) {
    return competencies;
  }

  return competencies.filter((competency) => {
    const name = getCompetencyName(competency as CompetencyDTO).toLowerCase();
    const externalId = (competency.externalId || '').toLowerCase();
    const description = (competency.description || '').toLowerCase();

    return name.includes(search) || externalId.includes(search) || description.includes(search);
  });
}

function resolveProjectAuditTypeStandardTemplateId(
  activity?: Pick<ActivityDTO, 'projectAuditType'> | null,
): number | undefined {
  return activity?.projectAuditType?.auditType?.standardTemplateId ?? undefined;
}

function resolveLegacyStandardTemplateId(
  activity?: Pick<ActivityDTO, 'standard'> | null,
): number | undefined {
  return activity?.standard?.standardTemplate?.id ?? undefined;
}

function resolveLegacyStandardSchemeLabel(
  activity?: Pick<ActivityDTO, 'standard'> | null,
): string | undefined {
  const standardCode = activity?.standard?.code?.trim();
  if (standardCode) {
    return standardCode;
  }

  const templateExternalId = activity?.standard?.standardTemplate?.externalId?.trim();
  if (templateExternalId) {
    return templateExternalId;
  }

  return undefined;
}

export function resolveActivitySchemeLabel(
  activity?: Pick<ActivityDTO, 'standard' | 'projectAuditType'> | null,
): string | undefined {
  if (activity?.projectAuditType != null) {
    const auditTypeTemplateId = resolveProjectAuditTypeStandardTemplateId(activity);
    const activityStandardTemplateId = resolveLegacyStandardTemplateId(activity);

    if (
      auditTypeTemplateId != null &&
      activityStandardTemplateId != null &&
      auditTypeTemplateId === activityStandardTemplateId
    ) {
      return resolveLegacyStandardSchemeLabel(activity);
    }

    return undefined;
  }

  return resolveLegacyStandardSchemeLabel(activity);
}

export function formatSchemeLabels(labels: Array<string | undefined>): string {
  const uniqueLabels = new Set<string>();

  for (const label of labels) {
    if (!label) {
      continue;
    }

    for (const part of label.split(',')) {
      const trimmed = part.trim();
      if (trimmed) {
        uniqueLabels.add(trimmed);
      }
    }
  }

  return Array.from(uniqueLabels).join(', ');
}

export function resolveBatchSchemeLabels(
  activities: Array<Pick<ActivityDTO, 'standard' | 'projectAuditType'>>,
): string {
  return formatSchemeLabels(activities.map((activity) => resolveActivitySchemeLabel(activity)));
}

export function resolveActivityStandardTemplateId(
  activity?: Pick<ActivityDTO, 'standard' | 'projectAuditType'> | null,
): number | undefined {
  if (activity?.projectAuditType != null) {
    return resolveProjectAuditTypeStandardTemplateId(activity);
  }

  return resolveLegacyStandardTemplateId(activity);
}

export function resolveBatchStandardTemplateIds(
  activities: Array<Pick<ActivityDTO, 'standard' | 'projectAuditType'>>,
): number[] {
  const templateIds = new Set<number>();

  for (const activity of activities) {
    const templateId = resolveActivityStandardTemplateId(activity);
    if (templateId != null) {
      templateIds.add(templateId);
    }
  }

  return Array.from(templateIds);
}

export function resolveBatchStandardTemplateId(
  activities: Array<Pick<ActivityDTO, 'standard' | 'projectAuditType'>>,
): number | undefined {
  const templateIds = resolveBatchStandardTemplateIds(activities);
  return templateIds.length === 1 ? templateIds[0] : undefined;
}

export function mergeCompetencyLists(competencyLists: ICompetencyItem[][]): ICompetencyItem[] {
  const byId = new Map<number | string, ICompetencyItem>();

  for (const competencies of competencyLists) {
    for (const competency of competencies) {
      byId.set(
        competency.id,
        mergeCompetencyItems(byId.get(competency.id) ?? competency, competency),
      );
    }
  }

  return Array.from(byId.values());
}
